<template>
	<BaseModal :show="show" border-classes="border-t-6 border-tertiary-light">
		<form class="flex flex-col h-fit bg-neutral">
			<div class="flex flex-row align-middle">
				<p class="text-xl font-bold leading-7 text-heading">Edit User</p>
				<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="ml-auto cursor-pointer" @click="cancel">
			</div>

			<div class="border-b border-neutral w-full h-[1px] my-6"></div>

			<div class="flex flex-col">
				<div class="flex space-x-5">
					<InputText class="flex-1" label="User ID" type="text" v-model="user_id" label-text-size="text-sm" input-height="h-11" disabled
									font-weight="font-normal" />

					<InputText class="flex-1" label="Registration" type="text" v-model="created_at" label-text-size="text-sm" input-height="h-11" disabled
									font-weight="font-normal" />
				</div>

				<div class="flex space-x-5">
					<InputText class="userpilot-mask flex-1" label="Email" type="text" placeholder="Enter email" icon="edit" label-text-size="text-sm" input-height="h-11"
									font-weight="font-normal"
									v-model="email" :errors="error" error-field="user.email" :disabled="!is_active" />

					<InputText class="userpilot-mask flex-1" label="Phone" type="tel" autocomplete="tel" placeholder="Enter 10-digit number, U.S. only, for example (999) 999-9999"
									icon="edit" label-text-size="text-sm" input-height="h-11" font-weight="font-normal"
									v-model="phone_number" v-maska:phone_number_unmasked.unmasked="'(###) ###-####'" :errors="error" error-field="user.phone_number"
									:disabled="!is_active" />
				</div>

				<div class="flex space-x-5">
					<InputText class="userpilot-mask flex-1" label="First name" type="text" placeholder="Enter first name" icon="edit" label-text-size="text-sm"
									input-height="h-11" font-weight="font-normal"
									v-model="first_name" :errors="error" error-field="user.given_name" :disabled="!is_active" />

					<InputText class="userpilot-mask flex-1" label="Last name" type="text" placeholder="Enter last name" icon="edit" label-text-size="text-sm" input-height="h-11"
									font-weight="font-normal"
									v-model="last_name" :errors="error" error-field="user.family_name" :disabled="!is_active" />
				</div>

				<div class="flex space-x-5" v-if="authStore.isPortalAdmin">
					<CheckBox input-id="cbx_portal_admin" label="Portal Administrator" label-location="left" class="mb-4" :checked="false" v-model="is_admin" :disabled="user_id === myUser?.id" />
				</div>

			</div>

			<div class="border-b border-neutral w-full h-[1px] mt-1 mb-5"></div>

			<div v-if="is_active" class="flex flex-row w-full mt-1">
				<div class="flex-grow">
					<SubmitButton v-if="user_id !== myUser?.id && authStore.isPortalAdmin" label="Disable" :type="ButtonType.Warning" :size="ButtonSize.Small"
									:disabled="isSaving || isDisabling" :loading="isDisabling" @click="toggleActivateAccount" />
				</div>
				<SubmitButton class="mr-4" label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" :disabled="isSaving || isDisabling" @click="cancel" />
				<SubmitButton v-if="is_active" label="Save" :size="ButtonSize.Small" :loading="isSaving" :disabled="isSaving || isDisabling" @click="saveClicked" />
			</div>
			<div v-else class="flex">
				<SubmitButton label="Activate" :type="ButtonType.Warning" :size="ButtonSize.Small" :disabled="isDisabling" :loading="isDisabling" @click="toggleActivateAccount" />
				<SubmitButton class="ml-auto" label="Close" :type="ButtonType.Outline" :size="ButtonSize.Small" :disabled="isDisabling" @click="cancel" />
			</div>
		</form>
	</BaseModal>
</template>

<script setup lang="js">
import InputText from '@/components/Forms/InputText.vue'
import { ref, watch } from 'vue'
import { vMaska } from 'maska/vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { userAdminApi } from '@/api/User/UserAdminApi.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { useAuthStore } from '@/stores/authStore.js'
import { userApi } from '@/api/User/UserApi.js'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import CheckBox from '@/components/Forms/CheckBox.vue'

const authStore = useAuthStore()
const myUser = authStore?.getAuth.user()
const isSaving = ref(false)
const isDisabling = ref(false)
const error = ref(null)
const user_id = ref(null)
const created_at = ref(null)
const email = ref(null)
const phone_number = ref(null)
const phone_number_unmasked = ref('')
const first_name = ref(null)
const last_name = ref(null)
const is_active = ref(true)
const activeStatusChanged = ref(false)
const is_admin = ref(false)

defineExpose({ phone_number_unmasked })

const props = defineProps({
	userToEdit: { type: Object, default: null },
	show: { type: Boolean, default: false }
})

const emit = defineEmits(['dismiss', 'showDiscardChangesModal', 'showToggleActivateAccountModal'])

watch(
	() => props.userToEdit,
	(newValue) =>
	{
		if (newValue)
		{
			handleUser(newValue)
		}
	}
)

function handleUser(p_user)
{
	user_id.value = p_user.id
	created_at.value = (new Date(p_user.created_at)).toLocaleString()
	email.value = p_user.email
	phone_number.value = p_user.phone_number
	first_name.value = p_user.given_name
	last_name.value = p_user.family_name
	is_active.value = p_user.deleted_at == null
	is_admin.value = p_user.is_admin === 1
}

function cancel()
{
	if (changesMade())
	{
		emit('showDiscardChangesModal', (discardChanges) =>
		{
			if (discardChanges)
			{
				emit('dismiss', activeStatusChanged.value)
			}
		})
	}
	else
	{
		emit('dismiss', activeStatusChanged.value)
	}
}

function changesMade()
{
	let user = props.userToEdit

	return (user?.email !== email.value ||
		user?.phone_number !== phone_number_unmasked.value ||
		user?.given_name !== first_name.value ||
		user?.family_name !== last_name.value ||
		user?.is_admin === 1 !== is_admin.value)
}

function toggleActivateAccount()
{
	if (is_active.value)
	{
		disable()
	}
	else
	{
		activate()
	}
}

function activate()
{
	emit('showToggleActivateAccountModal', true, (toggleActivateAccount) =>
	{
		if (toggleActivateAccount)
		{
			error.value = null
			isDisabling.value = true

			userAdminApi.restore(props.userToEdit?.id, (p_user) =>
			{
				handleUser(p_user)
				isDisabling.value = false
				activeStatusChanged.value = true
			}, (e) =>
			{
				isDisabling.value = false
				error.value = e.showErrorBanner()
			})
		}
	})
}

function disable()
{
	emit('showToggleActivateAccountModal', false, (toggleActivateAccount) =>
	{
		if (toggleActivateAccount)
		{
			error.value = null
			isDisabling.value = true

			userAdminApi.delete(props.userToEdit?.id, (p_user) =>
			{
				handleUser(p_user)
				isDisabling.value = false
				activeStatusChanged.value = true
			}, (e) =>
			{
				isDisabling.value = false
				error.value = e.showErrorBanner()
			})
		}
	})
}

function saveClicked()
{
	if (!changesMade())
	{
		cancel()
		return
	}

	error.value = null
	isSaving.value = true

	if (user_id.value === myUser.id)
	{
		updateCurrentUser()
	}
	else
	{
		updateOtherUser()
	}
}

/**
 * Updates a user's info that is not the currently logged-in user;
 */
function updateOtherUser()
{
	const user = {
		'id': user_id.value,
		'email': email.value,
		'given_name': first_name.value,
		'family_name': last_name.value,
		'phone_number': phone_number_unmasked.value
	};

	if (authStore.isPortalAdmin)
	{
		user['is_admin'] = is_admin.value ? 1 : 0;
	}

	userAdminApi.update({
		'user': user
	}, () =>
	{
		isSaving.value = false
		emit('dismiss', true)
	}, (e) =>
	{
		isSaving.value = false
		error.value = e.showErrorBanner()
	})
}

/**
 * Updates the currently logged-in user if they change their own info and
 * also updates their jwt data.
 */
function updateCurrentUser()
{
	userApi.update({
		'user': {
			'id': user_id.value,
			'email': email.value,
			'given_name': first_name.value,
			'family_name': last_name.value,
			'phone_number': phone_number_unmasked.value
		}
	}, (p_response) =>
	{
		if (authStore.handleUpdateResult(p_response.body())) {
			isSaving.value = false
			emit('dismiss', true)
		}
	}, (e) =>
	{
		isSaving.value = false
		error.value = e.showErrorBanner()
	})
}

</script>